import { PortalMessage } from './store/portal-messages.model';

export interface SecureMessageCategory {
  key: string;
  labelKey: string;
  /** Message moduleType values (see GeneralPublic ModuleType enum) that belong to this category. */
  moduleTypes: string[];
  /** Only enabled categories are navigable; the others are displayed as "coming soon". */
  enabled: boolean;
}

export interface SecureMessageGroup {
  key: string;
  labelKey: string;
  hidden?: boolean;
  categories: SecureMessageCategory[];
}

/**
 * Single source of truth for the "Messages sécurisés" navigation tree.
 * To activate a new category later, set `enabled: true` — the menu, routes,
 * filtering and unread counters all derive from this list.
 */
export const SECURE_MESSAGE_GROUPS: SecureMessageGroup[] = [
  {
    key: 'fonds-assurance',
    labelKey: 'secureMessages.groups.insuranceFund',
    categories: [
      {
        key: 'reclamations',
        labelKey: 'secureMessages.categories.claims',
        moduleTypes: ['FarpReclamation'],
        enabled: true
      },
      {
        key: 'communication',
        labelKey: 'secureMessages.categories.communication',
        moduleTypes: ['FarpCommunication'],
        enabled: true
      }
    ]
  },
  {
    key: 'ordre-des-dentistes',
    labelKey: 'secureMessages.groups.orderOfDentists',
    // Temporarily hidden for the upcoming deployment: only the Fonds d'assurance features are released.
    hidden: true,
    categories: [
      { key: 'admission-tableau', labelKey: 'secureMessages.categories.admission', moduleTypes: [], enabled: false },
      { key: 'exercice-en-societe', labelKey: 'secureMessages.categories.practiceInCompany', moduleTypes: [], enabled: false },
      { key: 'inspection-professionnelle', labelKey: 'secureMessages.categories.inspection', moduleTypes: [], enabled: false },
      {
        key: 'syndic',
        labelKey: 'secureMessages.categories.syndic',
        moduleTypes: ['SyndicCommunication', 'SyndicInquiry', 'SyndicIncident', 'SyndicDisciplinaryProcedure'],
        enabled: false
      },
      { key: 'formation-continue', labelKey: 'secureMessages.categories.continuingEducation', moduleTypes: [], enabled: false },
      { key: 'autres-communications', labelKey: 'secureMessages.categories.other', moduleTypes: [], enabled: false }
    ]
  }
];

export const VISIBLE_SECURE_MESSAGE_GROUPS: SecureMessageGroup[] = SECURE_MESSAGE_GROUPS.filter(g => !g.hidden);

export interface SecureMessageSelection {
  group: SecureMessageGroup;
  category: SecureMessageCategory;
}

export const DEFAULT_SECURE_MESSAGE_LINK = ['/messages', 'fonds-assurance', 'reclamations'];

export function findSecureMessageCategory(groupKey: string | null, categoryKey: string | null): SecureMessageSelection | null {
  const group = VISIBLE_SECURE_MESSAGE_GROUPS.find(g => g.key === groupKey);
  const category = group?.categories.find(c => c.key === categoryKey);
  return group && category ? { group, category } : null;
}

export function messageMatchesCategory(message: Pick<PortalMessage, 'moduleType'>, category: SecureMessageCategory): boolean {
  const type = (message.moduleType ?? '').toLowerCase();
  return category.moduleTypes.some(t => t.toLowerCase() === type);
}

export function enabledSecureMessageCategories(): SecureMessageCategory[] {
  return VISIBLE_SECURE_MESSAGE_GROUPS.flatMap(g => g.categories).filter(c => c.enabled);
}
